import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/api/client";
import { listOrganizations, type OrgSummary } from "@/api/mailbox-access";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableBodySkeleton } from "@/components/ui/skeleton";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import { useAuth } from "@/stores/auth";

const ORGS_QUERY = ["organizations"] as const;

interface CreateOrgForm {
  name: string;
  slug: string;
  admin_name: string;
  admin_email: string;
  admin_password: string;
}

interface OrgCreateResponse {
  organization: OrgSummary;
  admin_user_id: string;
  default_group_id: string;
  default_sla_policy_id: string;
}

function OrganizationsPage() {
  const { data: orgs, isLoading } = useQuery({
    queryKey: ORGS_QUERY,
    queryFn: listOrganizations,
  });

  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium tracking-tight">Organizations</h1>
          <p className="mt-1 text-sm text-text-muted">
            Tenants on this platform. Each organization gets its own admin, default
            group, and default SLA policy on creation.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          New organization
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-surface">
        {isLoading ? (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Slug</TH>
                <TH>ID</TH>
              </TR>
            </THead>
            <TableBodySkeleton rows={4} cells={3} />
          </Table>
        ) : !orgs || orgs.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
              <Building2 className="h-4 w-4 text-text-muted" />
            </div>
            <div className="text-sm font-medium">No organizations yet</div>
            <div className="mt-1 text-xs text-text-muted">
              Click <span className="font-mono">New organization</span> to create one.
            </div>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Slug</TH>
                <TH>ID</TH>
              </TR>
            </THead>
            <TBody>
              {orgs.map((o) => (
                <TR key={o.id}>
                  <TD className="font-medium">{o.name}</TD>
                  <TD className="text-text-muted font-mono text-xs">{o.slug}</TD>
                  <TD className="text-text-faint font-mono text-xs">{o.id}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <CreateOrgDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function CreateOrgDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<CreateOrgForm>({
    defaultValues: {
      name: "",
      slug: "",
      admin_name: "",
      admin_email: "",
      admin_password: "",
    },
  });
  const [slugDirty, setSlugDirty] = useState(false);
  const watchedName = form.watch("name");

  useEffect(() => {
    if (!slugDirty) {
      form.setValue("slug", slugify(watchedName));
    }
  }, [watchedName, slugDirty, form]);

  const mutation = useMutation({
    mutationFn: (values: CreateOrgForm) =>
      apiFetch<OrgCreateResponse>("/api/organizations", {
        method: "POST",
        json: {
          name: values.name.trim(),
          slug: values.slug.trim().toLowerCase(),
          admin: {
            name: values.admin_name.trim(),
            email: values.admin_email.trim().toLowerCase(),
            password: values.admin_password,
          },
        },
      }),
    onSuccess: (created) => {
      toast.success(
        `Created ${created.organization.slug} — admin can log in now`,
      );
      void qc.invalidateQueries({ queryKey: ORGS_QUERY });
      onOpenChange(false);
      form.reset();
      setSlugDirty(false);
    },
    onError: (e) => {
      toast.error(
        e instanceof ApiError ? e.message : "Could not create organization",
      );
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) {
          form.reset();
          setSlugDirty(false);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New organization</DialogTitle>
          <DialogDescription>
            Creates the org plus a first admin, a default group, and a default SLA
            policy (24h first response / 72h resolution).
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="flex flex-col gap-3"
        >
          <FormField label="Organization name">
            <Input
              {...form.register("name", { required: true, minLength: 1 })}
              placeholder="Acme, Inc."
              autoFocus
            />
          </FormField>
          <FormField label="Slug" hint="Lowercase letters, numbers, dashes.">
            <Input
              {...form.register("slug", {
                required: true,
                pattern: /^[a-z0-9-]+$/,
              })}
              placeholder="acme"
              onChange={(e) => {
                setSlugDirty(true);
                form.setValue("slug", e.target.value);
              }}
            />
          </FormField>
          <div className="mt-2 border-t border-border pt-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-faint">
              First admin
            </p>
          </div>
          <FormField label="Admin name">
            <Input
              {...form.register("admin_name", { required: true, minLength: 1 })}
              placeholder="Jane Doe"
            />
          </FormField>
          <FormField label="Admin email">
            <Input
              type="email"
              {...form.register("admin_email", { required: true })}
              placeholder="jane@acme.com"
            />
          </FormField>
          <FormField label="Admin password" hint="Minimum 8 characters.">
            <Input
              type="text"
              {...form.register("admin_password", {
                required: true,
                minLength: 8,
              })}
              placeholder="something-memorable"
            />
          </FormField>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

export const Route = createFileRoute("/_auth/settings/organizations")({
  beforeLoad: () => {
    const role = useAuth.getState().user?.role;
    if (role !== "superadmin") {
      throw redirect({ to: "/inbox" });
    }
  },
  component: OrganizationsPage,
});
